import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import {
  useMyMonitoringGetMyMonitoringUser,
  useMyMonitoringRemoveMyMonitoringUser,
  useMyMonitoringUpdateMyMonitoringUser
} from '@apps-common/apis/my-monitoring/users'
import {
  useGetClientAccountAssociation,
  useRemoveClientAccountSite,
  useUpdateClientAccountAssociation
} from '@apps-common/apis/portal/client-accounts'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputInfoSM, InputSM } from '@apps-common/components/elements/form'
import { VertSpacerLg } from '@apps-common/components/layout'
import { ConfirmModalAction } from '@apps-common/components/modals'
import { ModalBody, ModalFooter, ModalTopConfirmActionButton } from '@apps-common/components/modals/modal'
import { MESSAGE_IS_MM_ADMIN } from '@apps-common/config/app-messages'
import { ModalDismiss } from '@apps-common/config/modal'
import { usePortalFirebaseUser } from '@apps-common/hooks/usePortalFirebaseUser'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { IMyMonitoringUserModel } from '@apps-common/types/portal/users'

interface IProps {
  userId: number
  unitId: string
  closeCb: (role: string) => void
}

export const MyMonitoringUserUpdateForm = ({ userId, unitId, closeCb }: IProps) => {
  const fbUser = usePortalFirebaseUser()
  const useGetAccountHook = useProjectHook(useGetClientAccountAssociation, useMyMonitoringGetMyMonitoringUser)
  const useUpdateUserHook = useProjectHook(useUpdateClientAccountAssociation, useMyMonitoringUpdateMyMonitoringUser)
  const useRemoveUserHook = useProjectHook(useRemoveClientAccountSite, useMyMonitoringRemoveMyMonitoringUser)

  const {
    data: user,
    isLoading: userIsLoading,
    isError: userIsError
  } = useGetAccountHook({
    config: {},
    userId,
    unitId
  })

  const { mutate: updateUser, isPending: updateUserIsPending } = useUpdateUserHook(true, () => closeCb(ModalDismiss.Confirm))
  const { mutate: removeUser, isPending: removeUserIsPending } = useRemoveUserHook(true, (result: boolean) => {
    if (result) {
      closeCb(ModalDismiss.Confirm)
    }
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset
  } = useForm<IMyMonitoringUserModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      id: userId,
      unitId
    }
  })

  useEffect(() => {
    if (user) {
      reset(user)
    }
  }, [user, reset])

  const submit = (values: IMyMonitoringUserModel) => {
    if (values.userPartition && values.userPartition.length > 0) {
      values.mapPartition = true
    }

    updateUser({ model: values })
  }

  const canDelete = useMemo(() => {
    return !!fbUser && fbUser.identityId !== userId
  }, [fbUser])

  const loading = !user || userIsLoading || userIsError
  const updating = isSubmitting || updateUserIsPending || removeUserIsPending

  return (
    <>
      <ModalBody classes='!pt-[95px]'>
        <div className='flex items-center justify-end'>
          <ModalTopConfirmActionButton
            loading={loading || updating || !canDelete}
            confirmProps={{
              text: 'Remove user?',
              description: 'Are you sure you would like to remove user from this site?',
              action: ConfirmModalAction.Error
            }}
            confirmCallback={() => removeUser({ userId, unitId })}
            text='Remove'
            icon='delete'
            classes='action-error'
          />
        </div>
        <form noValidate id='my-monitoring-user-update-existing-form' onSubmit={handleSubmit(submit)}>
          <InputInfoSM id='user-email' label='Email' showLabel loading={loading} value={user?.email} />
          <InputInfoSM id='user-name' label='Name' showLabel loading={loading} value={`${user?.firstName} ${user?.lastName}`} />
          <InputSM
            key='userNumber'
            type='text'
            placeholder='1 to 999'
            label='User number (Zone)'
            id='userNumber'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('userNumber', {
              required: true,
              pattern: {
                value: /^[0-9]{3}$/,
                message: '3 numerical characters only e.g. 001'
              }
            })}
            errors={errors.userNumber}
          />
          <InputSM
            key='userPartition'
            type='text'
            placeholder='0 to 99'
            label='User partition (If required)'
            id='userPartition'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('userPartition', {
              required: false,
              pattern: {
                value: /^[0-9]{2}$/,
                message: '2 numerical characters only e.g. 01'
              }
            })}
            errors={errors.userPartition}
          />
          <CheckboxInputSM
            label='Site administrator'
            id='isAdmin'
            loading={loading}
            disabled={updating}
            registration={register('isAdmin')}
            errors={errors.isAdmin}
            info={MESSAGE_IS_MM_ADMIN}
          />
          <VertSpacerLg />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-between gap-2'>
          <ButtonRoundedSm
            type='submit'
            form='my-monitoring-user-update-existing-form'
            loading={loading || updating}
            text='Update'
            className='action-success'
          />
          <ButtonRoundedSm
            type='button'
            loading={loading || updating}
            text='Cancel'
            className='action-neutral'
            callback={() => closeCb(ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </>
  )
}
